import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { trackLeadSubmit } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import PromoCountdown from '../../components/course/PromoCountdown';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import { ROB_LINKEDIN_URL } from '../../components/course/courseContent';
import { useEducationDesignVariant } from '../education/EducationDesignExperiment';
import {
  SmsConsentCheckbox,
  buildSmsConsentPayload,
  shouldShowSmsConsent,
} from '../../components/SmsConsent';
import HowFoundUsDropdown from '../../components/CheckoutFormShared/HowFoundUsDropdown';
import { sanitizeInternalDestination } from '../../utils/postSignupJourney';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import {
  buildChallengeCalendarLinks,
  AI_TRADING_CHALLENGE_NAME,
  canonicalChallengeRunName,
  canonicalChallengeSessionTitle,
  formatChallengeDate,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  isChallengeReplayAvailable,
  splitDisplayName,
  selectChallengeRun,
  youtubeLiveUrl,
} from './challengePageUtils';
import '../courses/CoursePage.css';
import './ChallengePage.css';
import './ChallengePageControlRoom.css';

const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';
const NEXT_CHALLENGE_CONTACT_URL =
  'mailto:support@botspot.trade?subject=Next%20AI%20Trading%20Challenge';
// Closing live session label. The clinic promises help and project reviews
// without requiring anyone to present a finished project.
const WRAP_UP_LABEL = 'Live Wrap-Up';

// Registration-page length policy (2026-08-07): our own PostHog test
// SALES_PAGE_DESIGN ended with the minimal hero+form control at a 26.59% Lead
// rate vs 21.50% for the decorated animated/FAQ variant and 9.68% for the
// video variant. Free lead capture converts best short: everything a visitor
// needs to say yes stays above or one scroll from the form.
const BOARD_STEPS = [
  {
    title: 'Set up together at kickoff',
    body: 'We set up your AI trading workflow together on camera. You leave with one clear project for the week.',
  },
  {
    title: 'Build one project during the week',
    body: 'Keep improving the same workflow. Short emails answer common setup questions. There is no daily homework.',
  },
  {
    title: 'Review it live with Rob',
    body: 'Come back live to review the workflow, get unstuck, and compare approaches. You can join even if the project is not finished.',
  },
];

const CHALLENGE_OUTCOMES = [
  {
    stage: 'Research',
    question: 'What changed?',
    detail: 'Market data · company context · news',
  },
  {
    stage: 'Plan',
    question: 'What would make this trade valid?',
    detail: 'Thesis · entry · risk · exit',
  },
  {
    stage: 'Order',
    question: 'Does every detail hold up?',
    detail: 'Paper or supported broker · final review',
  },
];

const CHALLENGE_LOGISTICS = [
  {
    name: 'Two live sessions with Rob',
    detail: `Kickoff and ${WRAP_UP_LABEL} on Zoom`,
  },
  {
    name: 'Challenge hub',
    detail: 'Live links, calendar links, and any approved recordings',
  },
  {
    name: 'Help between sessions',
    detail: 'Short emails with setup help and next steps',
  },
];

const CHALLENGE_REQUIREMENTS = [
  {
    name: 'A computer and internet connection',
    detail: 'Both live sessions run on Zoom. No coding experience is required.',
  },
  {
    name: 'A paper or supported brokerage account',
    detail: 'Needed only when you are ready to prepare a paper or real order.',
  },
];

// Five objection-killers only. Time commitment lives in the board copy, so it
// is not repeated here.
const FAQS = [
  {
    q: 'Is this really free?',
    a: 'Yes. Both live sessions and the week of tip emails are free. No credit card.',
  },
  {
    q: 'Do I need to know how to code?',
    a: 'No. Use BotSpot in plain English. Advanced users can connect their own coding agent, but nothing in the challenge requires it.',
  },
  {
    q: 'Do I need a broker?',
    a: 'To place a paper or real trade during the challenge, connect a supported broker. Rob covers setup at kickoff.',
  },
  {
    q: 'What if I miss the kickoff?',
    a: 'Join the Live Wrap-Up and use the same hub. If an approved recording is published, it will appear there.',
  },
  {
    q: 'Will you pitch something?',
    a: 'You will work on something real in both sessions. At the end, Rob briefly explains the paid course options. No purchase is required.',
  },
];

const CONTROL_ROOM_FAQS = FAQS;

const OptionalRegistrationFields = ({ form, setForm, disabled, testId, tone = 'dark' }) => (
  <>
    <label>
      Mobile number (optional)
      <input
        type="tel"
        name="phone"
        autoComplete="tel"
        value={form.phone}
        onChange={(e) => {
          const phone = e.target.value;
          setForm({
            ...form,
            phone,
            marketingSms: shouldShowSmsConsent(phone) ? form.marketingSms : false,
          });
        }}
        disabled={disabled}
        placeholder="+1 555 555 5555"
      />
    </label>
    <SmsConsentCheckbox
      id={`${testId}-marketing-sms`}
      checked={form.marketingSms}
      onChange={(checked) => setForm({ ...form, marketingSms: checked })}
      disabled={disabled || !form.phone.trim()}
      tone={tone}
      compact
    />
  </>
);

const RegistrationForm = ({
  form,
  setForm,
  onSubmit,
  submitting,
  error,
  disabled,
  disabledReason,
  ctaLabel,
  testId,
  requireAttribution,
  postPurchase = false,
  compact = false,
}) => (
  <form
    className={`cp-form chp-form-card${postPurchase ? ' chp-form-card--post-purchase' : ''}`}
    onSubmit={onSubmit}
    data-testid={testId}
    autoComplete="on"
  >
    <h3>
      {postPurchase ? 'Join the free challenge' : compact ? 'Register free' : 'Save your free seat'}
    </h3>
    <label>
      Your name
      <input
        type="text"
        name="name"
        required
        autoComplete="name"
        placeholder="First and last name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={disabled}
      />
    </label>
    <label>
      Email
      <input
        type="email"
        name="email"
        required
        autoComplete="email"
        placeholder="you@example.com"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        disabled={disabled}
      />
    </label>
    {requireAttribution ? (
      <HowFoundUsDropdown
        id={`${testId}-how-found-us`}
        value={form.howFoundUs}
        otherText={form.howFoundUsDetails}
        onChange={(howFoundUs) =>
          setForm({
            ...form,
            howFoundUs,
            howFoundUsDetails: howFoundUs === 'other' ? form.howFoundUsDetails : '',
          })
        }
        onOtherTextChange={(howFoundUsDetails) => setForm({ ...form, howFoundUsDetails })}
        disabled={disabled || submitting}
      />
    ) : null}
    <OptionalRegistrationFields
      form={form}
      setForm={setForm}
      disabled={disabled}
      testId={testId}
      tone={compact ? 'light' : 'dark'}
    />
    {disabled && disabledReason ? (
      <p className="chp-unavailable" role="status">
        {disabledReason}
      </p>
    ) : null}
    <button
      type="submit"
      className="cp-btn-primary"
      disabled={submitting || disabled}
      data-testid={`${testId}-submit`}
      title={disabled ? disabledReason : undefined}
    >
      {submitting ? 'Saving your seat...' : ctaLabel}
    </button>
    {error ? (
      <p className="cp-note" role="alert">
        {error}
      </p>
    ) : null}
    <p className="chp-reassure">Free. No credit card. No coding required.</p>
  </form>
);

/**
 * Free challenge landing page and, once registered, the participant hub.
 *
 * Sessions come from the course / session-group / course-session tables.
 * If the catalog cannot load, purchase/registration is disabled loudly.
 */
const ChallengePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const educationDesignVariant = useEducationDesignVariant();
  const controlRoom = educationDesignVariant === 'control_room';
  // Experiment ends at challenge landing page. Linked Education pages never
  // inherit treatment query parameters or evaluate challenge flag.
  const educationCoursePath = LIVE_COURSE_PATH;
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    loginWithRedirect,
    getApiToken,
  } = useUser();
  const [challenge, setChallenge] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState({
    description: '',
    publicLink: '',
    question: '',
    displayName: '',
    showcasePermission: false,
    screenshotFile: null,
  });
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [submissionSaved, setSubmissionSaved] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    marketingSms: false,
    howFoundUs: '',
    howFoundUsDetails: '',
  });
  const isPostPurchase = searchParams.get('post_purchase') === 'true';
  const postPurchaseDestination = sanitizeInternalDestination(
    searchParams.get('redirectTo'),
    '/account-settings/subscriptions',
  );
  const requireAttribution = !String(user?.howFoundUs || '').trim();

  useEffect(() => {
    if (!controlRoom || registered) return undefined;

    const root = document.getElementById('challenge-top');
    const motionItems = Array.from(root?.querySelectorAll('[data-chx-reveal]') || []);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const supportsIntersectionObserver = 'IntersectionObserver' in window;

    if (reducedMotion || !supportsIntersectionObserver) {
      motionItems.forEach((item) => item.setAttribute('data-chx-reveal', 'visible'));
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-chx-reveal', 'visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' },
    );

    motionItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [controlRoom, registered]);

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const token = isAuthenticated ? await getApiToken().catch(() => null) : null;
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
        // Public filter keeps entitled cohorts from burying challenge runs.
        const listRes = await fetch(
          `${API_URL}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
          { headers: authHeaders },
        );
        if (!listRes.ok) throw new Error(`session_groups_${listRes.status}`);
        const listData = await listRes.json();
        const groups = Array.isArray(listData)
          ? listData
          : listData.sessionGroups || listData.data || [];

        const match = selectChallengeRun(groups, slug);
        if (!match) throw new Error('challenge_not_found');

        let detail = match;
        const detailRes = await fetch(`${API_URL}/courses/session-groups/${match.id}`, {
          headers: authHeaders,
        });
        if (detailRes.ok) {
          detail = await detailRes.json();
        }

        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setRegistered(Boolean(detail?.isRegistered || detail?.registration));
        setLoadError(null);
      } catch (err) {
        if (!mounted) return;
        setChallenge(null);
        setLoadError(
          'Challenge schedule is temporarily unavailable. Registration is disabled until dates load from the catalog.',
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug, isAuthenticated, getApiToken]);

  useEffect(() => {
    if (user?.email) {
      setForm((prev) => {
        const profileName =
          [user.firstName, user.lastName].filter(Boolean).join(' ') ||
          String(user.name || '').trim();
        const profilePhone = String(user.phone || '').trim();
        const shouldUseProfilePhone = !prev.phone && Boolean(profilePhone);

        return {
          ...prev,
          email: prev.email || user.email,
          name: prev.name || profileName,
          phone: prev.phone || profilePhone,
          // Consent follows the saved profile only when the saved phone is the
          // value being filled. A visitor-edited number keeps its own decision.
          marketingSms: shouldUseProfilePhone
            ? user.smsMarketingConsent === true
            : prev.marketingSms,
        };
      });
    }
  }, [user]);

  useEffect(() => {
    if (!registered || !isAuthenticated || !challenge?.id) return undefined;
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const token = await getApiToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mounted) return;
        const payload = await res.json();
        if (!payload?.submission) return;
        setSubmission((current) => ({
          ...current,
          description: payload.submission.description || '',
          publicLink: payload.submission.publicLink || '',
          question: payload.submission.question || '',
          displayName: payload.submission.displayName || '',
          showcasePermission: payload.submission.showcasePermission === true,
        }));
        setSubmissionSaved(true);
      } catch {
        // An unavailable optional share form must not hide the challenge hub.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [registered, isAuthenticated, challenge?.id, getApiToken]);

  const { kickoff, demoDay, sessions } = useMemo(
    () => pickKickoffAndDemo(challenge?.sessions || []),
    [challenge],
  );

  // List payload may only expose nextSession when detail is down.
  const kickoffAt =
    kickoff?.scheduledAt || challenge?.nextSession?.scheduledAt || challenge?.scheduledAt || null;
  const demoAt = demoDay?.scheduledAt || null;

  const kickoffLabel = formatChallengeDateTime(kickoffAt);
  const demoLabel = formatChallengeDateTime(demoAt);
  const kickoffCalendarLinks = kickoff
    ? buildChallengeCalendarLinks(kickoff, getApiBaseUrl() || 'https://api.botspot.trade')
    : null;
  const kickoffTime = kickoffAt ? new Date(kickoffAt).getTime() : Number.NaN;
  const hasFutureKickoff = Number.isFinite(kickoffTime) && kickoffTime > Date.now();
  const registrationOpen =
    !loadError &&
    Boolean(challenge?.id) &&
    challenge?.registrationAccess === 'public' &&
    hasFutureKickoff;
  const registrationStatus = loadError
    ? {
        title: 'Schedule could not load',
        body: 'Registration stays locked until current dates load from the catalog.',
      }
    : kickoffAt && !hasFutureKickoff
      ? {
          title: 'This challenge has started',
          body: 'Ask about the next live run, or compare the five-week course.',
        }
      : {
          title: 'Next dates not posted',
          body: 'Registration opens when kickoff and Live Wrap-Up dates are published.',
        };

  const details = useMemo(
    () => [
      {
        label: 'Live kickoff',
        value: kickoffLabel || (isLoading ? 'Loading...' : 'Unavailable'),
      },
      { label: 'Length', value: '7 days' },
      { label: 'Price', value: 'Free' },
      {
        label: WRAP_UP_LABEL,
        value: demoLabel || (isLoading ? 'Loading...' : 'One week later'),
      },
    ],
    [kickoffLabel, demoLabel, isLoading],
  );

  const register = useCallback(
    async (event) => {
      event.preventDefault();
      const utmData = getUtmParamsForApi() || {};
      // A valid email submit is one generic Lead for every ad platform. Do not
      // wait for HTTP 201, howFoundUs matching, or another server response.
      trackLeadSubmit({
        source: window.location.pathname,
        slug,
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_term: utmData.utm_term,
        utm_content: utmData.utm_content,
        gclid: utmData.gclid,
        gbraid: utmData.gbraid,
        wbraid: utmData.wbraid,
        google_campaign_id: utmData.utm_campaign_id,
        google_asset_group_id: utmData.utm_asset_group,
        google_creative_id: utmData.utm_creative,
        google_device: utmData.utm_device,
        google_network: utmData.utm_network,
        google_placement: utmData.utm_placement,
        google_physical_location_id: utmData.utm_location,
      });
      setSubmitting(true);
      setError(null);
      const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

      try {
        if (!challenge?.id || !registrationOpen) {
          throw new Error('challenge_not_ready');
        }
        const { firstName, lastName } = splitDisplayName(form.name);
        const phone = String(form.phone || '').trim();
        const posthogDistinctId = window.posthog?.get_distinct_id?.();
        const body = {
          firstName,
          lastName,
          email: form.email,
          registrationContext: 'public_challenge',
          posthogDistinctId: posthogDistinctId?.slice?.(0, 200),
          discoverySource: form.howFoundUs || undefined,
          discoveryDetails: form.howFoundUsDetails || undefined,
          utmSource: utmData.utm_source || undefined,
          utmMedium: utmData.utm_medium || undefined,
          utmCampaign: utmData.utm_campaign || undefined,
          utmTerm: utmData.utm_term || undefined,
          utmContent: utmData.utm_content || undefined,
          gclid: utmData.gclid || undefined,
          gbraid: utmData.gbraid || undefined,
          wbraid: utmData.wbraid || undefined,
          fbclid: utmData.fbclid || undefined,
          fbp: utmData.fbp || undefined,
          fbc: utmData.fbc || undefined,
          googleCampaignId: utmData.utm_campaign_id || undefined,
          googleAssetGroupId: utmData.utm_asset_group || undefined,
          googleCreativeId: utmData.utm_creative || undefined,
          googleDevice: utmData.utm_device || undefined,
          googleNetwork: utmData.utm_network || undefined,
          googlePlacement: utmData.utm_placement || undefined,
          googlePhysicalLocationId: utmData.utm_location || undefined,
          metaCampaignId: utmData.utm_campaign_id || undefined,
          metaAdSetId: utmData.utm_adset_id || undefined,
          metaAdId: utmData.utm_ad_id || undefined,
          firstTouchAttribution: Object.fromEntries(
            Object.entries(utmData).filter(
              ([key, value]) => key.startsWith('first_touch_') && value,
            ),
          ),
          referrer: utmData.referrer || document.referrer || undefined,
          landingPage: utmData.landing_page || window.location.href.split('?')[0],
        };

        // Phone is optional. Only attach phone + SMS consent proof when the
        // visitor actually gave a number. Unchecked still means an explicit
        // marketing decline for that number. No phone => no SMS consent row.
        if (phone) {
          body.phone = phone;
          // ABSOLUTE: customer SMS opt-in is ONLY full marketing consent.
          // Never send a separate event-only / "this challenge only" consent
          // payload. Node persists user.smsMarketingConsent* +
          // sms_marketing_consent_event, and gates challenge reminders on that
          // same marketing decision. Lawsuit-risk ban: 2026-08-08.
          body.smsMarketingConsent = buildSmsConsentPayload({
            source: 'ai_trading_challenge_registration',
            phoneNumber: phone,
            accepted: form.marketingSms,
            pagePath: window.location.pathname,
          });
        }

        const token = isAuthenticated ? await getApiToken().catch(() => null) : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/register`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload.message || payload.error || 'registration_failed');
        }

        const payload = await res.json().catch(() => ({}));
        // A 201 means the seat is saved. Google Ads already fired on click.
        // Keep booking independent from authentication. Session storage
        // prefills Calendly without putting personal data in URL.
        window.sessionStorage.setItem(
          'challengeBookingContext',
          JSON.stringify({
            email: form.email,
            name: form.name,
            registrationId: payload?.registration?.id || null,
            slug,
          }),
        );
        navigate(`/challenges/${slug}/welcome`);
      } catch (err) {
        setError(
          err?.message && !String(err.message).includes('_')
            ? err.message
            : 'That did not go through. Please try again, or email support@botspot.trade and we will add you manually.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [form, slug, challenge, registrationOpen, isAuthenticated, getApiToken, navigate],
  );

  const saveSubmission = useCallback(
    async (event) => {
      event.preventDefault();
      setSubmissionSaving(true);
      setSubmissionError(null);
      const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

      try {
        const token = await getApiToken();
        if (!token) throw new Error('Sign in before sharing your project.');

        let screenshotUrl;
        if (submission.screenshotFile) {
          const uploadBody = new FormData();
          uploadBody.append('file', submission.screenshotFile);
          const uploadRes = await fetch(
            `${API_URL}/courses/session-groups/${challenge.id}/submission-image`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: uploadBody,
            },
          );
          const uploadPayload = await uploadRes.json().catch(() => ({}));
          if (!uploadRes.ok) {
            throw new Error(uploadPayload.error || 'Could not upload that screenshot.');
          }
          screenshotUrl = uploadPayload.screenshotUrl;
        }

        const res = await fetch(`${API_URL}/courses/session-groups/${challenge.id}/submission`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            description: submission.description,
            publicLink: submission.publicLink || undefined,
            screenshotUrl,
            question: submission.question || undefined,
            displayName: submission.displayName || undefined,
            showcasePermission: submission.showcasePermission,
          }),
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload.error || 'Could not save your project.');
        setSubmissionSaved(true);
        setSubmission((current) => ({ ...current, screenshotFile: null }));
      } catch (err) {
        setSubmissionError(err?.message || 'Could not save your project.');
      } finally {
        setSubmissionSaving(false);
      }
    },
    [challenge?.id, getApiToken, submission],
  );

  const focusRegistrationForm = () => {
    window.requestAnimationFrame(() => {
      document.querySelector('#challenge-register input:not([disabled])')?.focus({
        preventScroll: true,
      });
    });
  };

  const runTitle = canonicalChallengeRunName(challenge?.name);
  const challengeName = String(
    challenge?.course?.title || challenge?.name || AI_TRADING_CHALLENGE_NAME,
  ).trim();
  const pageTitle = `${challengeName} | Free Live Challenge | BotSpot`;
  const pageDescription = controlRoom
    ? 'One live week to research an idea, define risk, and prepare an order you can explain.'
    : String(challenge?.description || '').trim() ||
      'Pick one investment. Research it with AI. Prepare a paper or real trade, then review the setup live with Rob.';
  const availableReplays = sessions.filter((session) => isChallengeReplayAvailable(session));

  return (
    <div
      className={`course-page challenge-page${controlRoom ? ' challenge-page--workbench' : ''}${isPostPurchase ? ' challenge-page--post-purchase' : ''}`}
      data-testid="challenge-page"
      id="challenge-top"
      role="main"
      tabIndex="-1"
    >
      <ScrollProgress />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      {isPostPurchase ? (
        <section className="chp-post-purchase" data-testid="post-purchase-challenge-context">
          <div className="chp-post-purchase__inner">
            <p className="chp-post-purchase__status">BotSpot plan active</p>
            <p className="chp-post-purchase__next">Join the free {challengeName}</p>
            <Link className="chp-post-purchase__skip" to={postPurchaseDestination}>
              Skip to BotSpot
            </Link>
          </div>
        </section>
      ) : null}

      <header
        className={
          controlRoom
            ? `chx-hero${isPostPurchase ? ' chx-hero--post-purchase' : ''}`
            : `chp-hero-grid${isPostPurchase ? ' chp-hero-grid--post-purchase' : ''}`
        }
      >
        <div className={controlRoom ? 'chx-hero__story' : 'chp-hero-copy'}>
          {controlRoom ? (
            <>
              <Reveal className="chx-hero__headline-reveal" delay={50}>
                <h1 className="chx-hero__title">
                  <span className="chx-hero__line">Take one trade</span>
                  <span className="chx-hero__line">from idea</span>
                  <span className="chx-hero__line">
                    to <span className="chx-hero__evidence">evidence.</span>
                  </span>
                </h1>
              </Reveal>
              <Reveal className="chx-hero__sub-reveal" delay={110}>
                <p className="chx-hero__sub">
                  Research it, define risk, and prepare an order during one free, focused week with
                  Rob.
                </p>
              </Reveal>
              <Reveal className="chx-hero__briefing-reveal" delay={150}>
                <section
                  className="chx-hero__briefing"
                  data-testid="challenge-hero-briefing"
                  aria-label="Challenge overview"
                >
                  <div className="chx-hero__host">
                    <a
                      className="chx-hero__host-photo"
                      href={ROB_LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View Rob Grzesik on LinkedIn"
                    >
                      <img
                        src="/images/rob.jpg"
                        alt="Rob Grzesik"
                        width="52"
                        height="52"
                        decoding="async"
                      />
                    </a>
                    <p>
                      <strong>
                        Live with{' '}
                        <a href={ROB_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                          Rob Grzesik
                        </a>
                      </strong>
                      <span>
                        CEO of BotSpot · creator of Lumibot · built 1,000+ trading systems
                      </span>
                    </p>
                  </div>
                  <p className="chx-hero__format">
                    Free to join · Seven focused days · No coding required
                  </p>
                  <div className="chx-hero__sessions" data-testid="challenge-hero-schedule">
                    <article>
                      <h2>Kickoff with Rob</h2>
                      <strong>
                        {kickoffLabel || (isLoading ? 'Loading date' : 'Next date coming')}
                      </strong>
                      <span>Choose and build</span>
                    </article>
                    <article>
                      <h2>{WRAP_UP_LABEL}</h2>
                      <strong>
                        {demoLabel || (isLoading ? 'Loading date' : 'Next date coming')}
                      </strong>
                      <span>Return and review</span>
                    </article>
                  </div>
                </section>
              </Reveal>
            </>
          ) : (
            <>
              <Reveal>
                <p className="chp-live-badge">
                  <span className="chp-live-dot" aria-hidden="true" />
                  Free · Seven days · Live with Rob
                </p>
              </Reveal>
              <Reveal delay={50}>
                <h1 className="chp-hero-title">{challengeName}</h1>
              </Reveal>
              <Reveal delay={90}>
                <p className="chp-hero-sub">{pageDescription}</p>
              </Reveal>
              <Reveal delay={120}>
                <p className="chp-when-line" data-testid="challenge-when">
                  {kickoffLabel && demoLabel
                    ? `Kickoff ${kickoffLabel} · ${WRAP_UP_LABEL} ${demoLabel}`
                    : kickoffLabel
                      ? `Kickoff ${kickoffLabel}`
                      : isLoading
                        ? 'Loading dates from catalog...'
                        : 'Dates unavailable from catalog'}
                </p>
              </Reveal>
              {isPostPurchase ? null : (
                <Reveal delay={140}>
                  <div className="chp-host-inline">
                    <a
                      className="chp-host-photo-link"
                      href={ROB_LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View Rob Grzesik on LinkedIn"
                    >
                      <img src="/images/rob.jpg" alt="Rob Grzesik" width="52" height="52" />
                    </a>
                    <div>
                      <strong>
                        <a href={ROB_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                          Rob Grzesik
                        </a>
                      </strong>
                      <span>
                        Managed $3B+ in transactions at Greystone · early Voyager Digital engineer
                        (company peaked near $1B) · 15+ years in financial technology
                      </span>
                    </div>
                  </div>
                </Reveal>
              )}
              {kickoffAt ? (
                <Reveal delay={160}>
                  <PromoCountdown endsAt={kickoffAt} label="Kickoff starts in" variant="blocks" />
                </Reveal>
              ) : null}
            </>
          )}
        </div>

        <div className={controlRoom ? 'chx-hero__desk' : 'chp-hero-form'}>
          {registered ? (
            <Reveal delay={120}>
              <div
                className="chp-registered"
                data-testid="challenge-registered"
                id="challenge-confirmation"
                role="status"
                tabIndex="-1"
              >
                <h2>You are in.</h2>
                <p>
                  {kickoffLabel ? `Kickoff is ${kickoffLabel}.` : 'Your challenge seat is saved.'}
                </p>
                <div className="chp-registered-actions" aria-label="Live kickoff links">
                  {kickoff?.joinUrl ? (
                    <a
                      className="chp-live-action chp-live-action--primary"
                      href={kickoff.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join live on Zoom
                    </a>
                  ) : null}
                  {youtubeLiveUrl(kickoff) ? (
                    <a
                      className="chp-live-action chp-live-action--secondary"
                      href={youtubeLiveUrl(kickoff)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get notified on YouTube
                    </a>
                  ) : null}
                </div>
                {kickoffCalendarLinks ? (
                  <div className="chp-registered-calendar">
                    <p>Add the kickoff to your calendar</p>
                    <div className="chp-registered-calendar-actions">
                      <a
                        href={kickoffCalendarLinks.google}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="challenge-registered-google-calendar"
                      >
                        Google Calendar
                      </a>
                      <a
                        href={kickoffCalendarLinks.ics}
                        download={`${canonicalChallengeSessionTitle(kickoff.title)}.ics`}
                        data-testid="challenge-registered-ics"
                      >
                        Apple / Outlook
                      </a>
                    </div>
                  </div>
                ) : null}
                {kickoffAt ? (
                  <div className="chp-registered-countdown">
                    <PromoCountdown
                      endsAt={kickoffAt}
                      label="Kickoff starts in"
                      variant={controlRoom ? 'words' : 'blocks'}
                    />
                  </div>
                ) : null}
              </div>
            </Reveal>
          ) : !isLoading && !registrationOpen ? (
            <div className="chp-registration-status" id="challenge-register" role="status">
              <h2>{registrationStatus.title}</h2>
              <p>{registrationStatus.body}</p>
              <div className="chp-registration-status__actions">
                <a className="cp-btn-primary" href={NEXT_CHALLENGE_CONTACT_URL}>
                  Ask about the next live week
                </a>
                <Link to={educationCoursePath}>See the five-week course</Link>
              </div>
            </div>
          ) : (
            <Reveal delay={120}>
              <div id="challenge-register">
                <RegistrationForm
                  form={form}
                  setForm={setForm}
                  onSubmit={register}
                  submitting={submitting}
                  error={error}
                  disabled={!registrationOpen || userLoading}
                  disabledReason={
                    userLoading
                      ? 'Checking registration details.'
                      : loadError ||
                        (!kickoffAt && !isLoading
                          ? 'Kickoff date is missing from the catalog, so registration is locked.'
                          : null)
                  }
                  ctaLabel={
                    isPostPurchase
                      ? 'Join the free challenge'
                      : controlRoom
                        ? 'Register free'
                        : 'Save my free seat'
                  }
                  testId="challenge-form"
                  requireAttribution={requireAttribution && !userLoading}
                  postPurchase={isPostPurchase}
                  compact={controlRoom}
                />
              </div>
            </Reveal>
          )}
        </div>
      </header>

      {controlRoom && !registered ? (
        <>
          <section
            className="chx-brief"
            data-testid="challenge-board"
            id="challenge-week"
            aria-labelledby="challenge-brief-heading"
          >
            <header className="chx-brief__heading chx-motion-heading" data-chx-reveal="pending">
              <h2 id="challenge-brief-heading">Follow the decision, not the noise.</h2>
              <p>Three checks turn one market idea into an order you can explain and review.</p>
            </header>

            <ol className="chx-brief__decisions" data-testid="challenge-outcome">
              {CHALLENGE_OUTCOMES.map((outcome, index) => (
                <li className="chx-motion-decision" data-chx-reveal="pending" key={outcome.stage}>
                  <span className="chx-brief__number" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <div className="chx-brief__decision-copy">
                    <h3>
                      {outcome.stage}: {outcome.question}
                    </h3>
                    <p>{outcome.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="chx-brief__gate chx-motion-gate" data-chx-reveal="pending">
            <strong>Final review</strong>
            <p>You review every detail before anything is sent.</p>
          </div>

          <section
            className="chx-brief__live chx-motion-live"
            data-chx-reveal="pending"
            data-testid="challenge-when"
          >
            <header>
              <h2>Seven days. Two live rooms.</h2>
            </header>
            <div className="chx-brief__live-track">
              <article>
                <h3>Kickoff with Rob</h3>
                <strong>
                  {kickoffLabel || (isLoading ? 'Loading next date' : 'Next date coming')}
                </strong>
                <p>Choose one idea and build its first working version together.</p>
              </article>
              <div className="chx-brief__duration" aria-hidden="true">
                <strong>7</strong>
                <span>days</span>
              </div>
              <article>
                <h3>{WRAP_UP_LABEL}</h3>
                <strong>
                  {demoLabel || (isLoading ? 'Loading next date' : 'Next date coming')}
                </strong>
                <p>Bring your decisions back. Review gaps and next moves with Rob.</p>
              </article>
            </div>
          </section>

          <aside
            className="chx-brief__practical chx-motion-practical"
            data-chx-reveal="pending"
            data-testid="challenge-includes"
          >
            <div>
              <h3>Included</h3>
              <ul>
                {CHALLENGE_LOGISTICS.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Bring</h3>
              <ul>
                {CHALLENGE_REQUIREMENTS.map((item) => (
                  <li key={item.name}>
                    <strong>{item.name}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </>
      ) : null}

      {controlRoom ? null : (
        <section className="chp-showcase" aria-label="What you will build">
          <Reveal>
            <img
              className="chp-showcase-art"
              src="/images/challenges/challenge-hero-dashboard.webp"
              alt="AI trading workflow with a watchlist, portfolio view, thesis, and sourced market updates"
              width="1376"
              height="768"
              loading="eager"
            />
            <p className="chp-showcase-caption">
              The project for the week: one AI trading workflow that researches the market, makes
              risk visible, and prepares a trade for your review.
            </p>
          </Reveal>
        </section>
      )}

      {controlRoom ? null : (
        <section
          className="chp-details"
          aria-label="Challenge details"
          data-testid="challenge-details"
        >
          <Reveal>
            <ul>
              {details.map((item) => (
                <li key={item.label}>
                  <span className="chp-details-label">{item.label}</span>
                  <span className="chp-details-value">{item.value}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </section>
      )}

      {controlRoom ? null : (
        <section className="cp-section" data-testid="challenge-board" id="challenge-week">
          <Reveal>
            <h2>How the week works</h2>
          </Reveal>
          <Reveal delay={50}>
            <p className="cp-sub">
              Two live sessions around one project. Set it up together, improve it during the week,
              then bring it back for review.
            </p>
          </Reveal>
          <ol className="chp-board-steps">
            {BOARD_STEPS.map((step, i) => (
              <Reveal as="li" key={step.title} delay={i * 70}>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </Reveal>
            ))}
          </ol>
          <div className="chp-mid-cta">
            <a href="#challenge-register" onClick={focusRegistrationForm}>
              Save my free seat
            </a>
          </div>
        </section>
      )}

      {controlRoom && !registered ? (
        <>
          <section
            className="chx-mentor chx-motion-mentor"
            data-chx-reveal="pending"
            data-testid="challenge-instructor"
            aria-labelledby="challenge-instructor-heading"
          >
            <figure className="chx-mentor__portrait">
              <a
                href={ROB_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Rob Grzesik on LinkedIn"
              >
                <img src="/images/rob.jpg" alt="Rob Grzesik" width="640" height="640" />
              </a>
            </figure>
            <div className="chx-mentor__story">
              <div className="chx-mentor__bio">
                <h2 id="challenge-instructor-heading">
                  Rob built trading systems before teaching them.
                </h2>
                <p>
                  Built Greystone loan software that processed $3B+ in transactions, then worked on
                  trading and brokerage infrastructure as an early Voyager Digital engineer.
                </p>
                <p>
                  Rob created Lumibot, built more than 1,000 trading systems, and taught algorithmic
                  and AI trading to more than 14,000 students.
                </p>
                <a
                  className="chx-mentor__link"
                  href={ROB_LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See Rob's experience
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
              <dl className="chx-mentor__proof">
                <div>
                  <dt>$3B+</dt>
                  <dd>transactions processed</dd>
                </div>
                <div>
                  <dt>1,000+</dt>
                  <dd>trading systems built</dd>
                </div>
                <div>
                  <dt>14,000+</dt>
                  <dd>students taught</dd>
                </div>
              </dl>
            </div>
          </section>
        </>
      ) : null}

      {registered ? (
        <>
          <section className="cp-section" data-testid="challenge-schedule">
            <Reveal>
              <h2>Your live schedule</h2>
            </Reveal>
            <ul className="chp-session-list">
              {sessions.map((session) => {
                const calendarLinks = buildChallengeCalendarLinks(
                  session,
                  getApiBaseUrl() || 'https://api.botspot.trade',
                );
                return (
                  <li key={session.id} data-testid={`challenge-session-${session.id}`}>
                    <div>
                      <strong>{canonicalChallengeSessionTitle(session.title)}</strong>
                      <span>{formatChallengeDateTime(session.scheduledAt)}</span>
                    </div>
                    <div className="chp-session-actions">
                      {session.joinUrl ? (
                        <a href={session.joinUrl} target="_blank" rel="noopener noreferrer">
                          Join on Zoom
                        </a>
                      ) : null}
                      {youtubeLiveUrl(session) ? (
                        <a
                          href={youtubeLiveUrl(session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`challenge-youtube-live-${session.id}`}
                        >
                          Get notified on YouTube
                        </a>
                      ) : null}
                      {calendarLinks ? (
                        <>
                          <a
                            href={calendarLinks.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`challenge-google-calendar-${session.id}`}
                          >
                            Google Calendar
                          </a>
                          <a
                            href={calendarLinks.ics}
                            download={`${canonicalChallengeSessionTitle(session.title)}.ics`}
                            data-testid={`challenge-ics-${session.id}`}
                          >
                            Apple / Outlook
                          </a>
                        </>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="cp-section" data-testid="challenge-includes">
            <Reveal>
              <h2>Support during the week</h2>
            </Reveal>
            <ul className="chp-includes">
              {CHALLENGE_LOGISTICS.map((item, i) => (
                <Reveal as="li" key={item.name} delay={i * 40}>
                  <strong>{item.name}</strong>
                  {' · '}
                  {item.detail}
                </Reveal>
              ))}
            </ul>
          </section>

          <section
            className="cp-section"
            data-testid="challenge-submission"
            id="challenge-submission"
          >
            <Reveal>
              <h2>Share your project (optional)</h2>
            </Reveal>
            <Reveal delay={50}>
              <p className="cp-sub">
                There is no submission requirement. Share a screenshot or public link only if you
                want feedback at the live wrap-up.
              </p>
            </Reveal>
            {isAuthenticated ? (
              <form className="chp-submission-form" onSubmit={saveSubmission}>
                <label>
                  What did you build?
                  <textarea
                    required
                    value={submission.description}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        description: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Public link (optional if you upload a screenshot)
                  <input
                    type="url"
                    value={submission.publicLink}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        publicLink: event.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Screenshot (optional if you add a public link)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        screenshotFile: event.target.files?.[0] || null,
                      })
                    }
                  />
                </label>
                <label>
                  Question for Rob (optional)
                  <input
                    type="text"
                    value={submission.question}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        question: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="cp-check">
                  <input
                    type="checkbox"
                    checked={submission.showcasePermission}
                    onChange={(event) =>
                      setSubmission({
                        ...submission,
                        showcasePermission: event.target.checked,
                      })
                    }
                  />
                  <span>BotSpot may show this project in the live wrap-up.</span>
                </label>
                <button type="submit" className="cp-btn-primary" disabled={submissionSaving}>
                  {submissionSaving ? 'Saving...' : 'Save my project'}
                </button>
                {submissionSaved ? <p role="status">Project saved.</p> : null}
                {submissionError ? <p role="alert">{submissionError}</p> : null}
              </form>
            ) : (
              <div className="chp-submission-signin">
                <p>Sign in with the same email you registered with to share your project.</p>
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() =>
                    loginWithRedirect({
                      appState: {
                        returnTo: `${window.location.pathname}${window.location.search}#challenge-submission`,
                      },
                    })
                  }
                >
                  Sign in to share
                </button>
              </div>
            )}
          </section>

          {availableReplays.length ? (
            <section className="cp-section">
              <Reveal>
                <h2>Replays</h2>
              </Reveal>
              <ul className="cp-archive">
                {availableReplays.map((s) => (
                  <Reveal as="li" key={s.id}>
                    <strong>{canonicalChallengeSessionTitle(s.title)}</strong>
                    <br />
                    <a href={s.recordingUrl} target="_blank" rel="noopener noreferrer">
                      Watch the recording
                    </a>
                  </Reveal>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : (
        <section className="cp-section cp-faq" data-testid="challenge-faq" id="challenge-questions">
          <Reveal>
            <h2>Questions</h2>
          </Reveal>
          {controlRoom ? (
            <div className="chx-faq__reveal" data-chx-reveal="pending">
              <div className="chp-faq-list">
                {CONTROL_ROOM_FAQS.map((item) => (
                  <details key={item.q}>
                    <summary>
                      <span>{item.q}</span>
                      <ChevronDown aria-hidden="true" size={20} strokeWidth={2} />
                    </summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ) : (
            <dl>
              {FAQS.map((item, i) => (
                <Reveal as="div" key={item.q} delay={i * 35}>
                  <dt>{item.q}</dt>
                  <dd>{item.a}</dd>
                </Reveal>
              ))}
            </dl>
          )}
        </section>
      )}

      {!registered && !controlRoom ? (
        <section className="cp-section" data-testid="challenge-final-cta">
          {/* No banner art here: the second form is the whole point of the
              section and decoration above it pushed it below the fold on
              mobile. */}
          <div className="chp-final">
            <div className="chp-final-inner">
              <Reveal>
                <h2>
                  {controlRoom
                    ? 'Build it this week. Keep using it next week.'
                    : 'One week from now, your AI trading workflow is ready to use'}
                </h2>
                <p>
                  {kickoffLabel
                    ? `Kickoff ${formatChallengeDate(kickoffAt)}. Free. No credit card.`
                    : 'Join the free live challenge when dates are available.'}
                </p>
              </Reveal>
              <Reveal delay={80}>
                <RegistrationForm
                  form={form}
                  setForm={setForm}
                  onSubmit={register}
                  submitting={submitting}
                  error={error}
                  disabled={!registrationOpen || userLoading}
                  disabledReason={userLoading ? 'Checking registration details.' : loadError}
                  ctaLabel="I'm in. Save my free seat"
                  testId="challenge-form-final"
                  requireAttribution={requireAttribution && !userLoading}
                />
              </Reveal>
              <p className="chp-secondary">
                Want the full five-week course after the live wrap-up?{' '}
                <Link to={educationCoursePath}>See the AI Trading Bootcamp</Link>
              </p>
            </div>
          </div>
        </section>
      ) : !registered && controlRoom ? (
        <section
          className="crx-closing chx-closing"
          data-chx-reveal="pending"
          aria-labelledby="crx-closing-heading"
          data-testid="challenge-closing"
        >
          <div className="chx-closing__statement">
            <h2 id="crx-closing-heading">
              {kickoffLabel ? 'Build one live.' : 'Build the next one live.'}
            </h2>
          </div>
          <div className="chx-closing__response">
            <p className="crx-closing-lede">
              {kickoffLabel
                ? `Kickoff ${formatChallengeDate(kickoffAt)}. Free. No credit card.`
                : "Next dates aren't posted yet. Ask for the schedule, or compare the five-week course."}
            </p>
            <div className="chx-closing__actions">
              {kickoffLabel ? (
                <a
                  className="cp-btn-primary"
                  href="#challenge-register"
                  onClick={focusRegistrationForm}
                >
                  Register free
                </a>
              ) : (
                <a className="cp-btn-primary" href={NEXT_CHALLENGE_CONTACT_URL}>
                  Ask for next dates
                </a>
              )}
              <Link to={educationCoursePath}>Compare the five-week course</Link>
            </div>
          </div>
        </section>
      ) : registered ? (
        <section className="cp-section cp-final">
          <Reveal>
            <h2>You are locked in for {runTitle}</h2>
            <p className="cp-muted">
              Watch your inbox for tips after kickoff.
              {demoLabel ? ` ${WRAP_UP_LABEL} is ${demoLabel}.` : ''}
            </p>
            {isPostPurchase ? (
              <Link
                className="cp-btn-primary"
                to={postPurchaseDestination}
                data-testid="post-purchase-challenge-continue"
              >
                Continue to BotSpot
              </Link>
            ) : null}
          </Reveal>
        </section>
      ) : null}

      <CourseLegalDisclaimer subject="This challenge" />
    </div>
  );
};

export default ChallengePage;
